// A PHOTO IN A MODEL'S WINDOW — the client's own framing (owner 2026-10-14:
// « …and the images that I want »).
//
// On a themed page (design system v2) each photo sits in a window the model
// drew: a polaroid's opening, a film frame, an arch… The window never moves
// or resizes (the frame around it is drawn for that box). What the client
// changes is WHICH photo is in it and WHICH PART of the photo shows:
//
//   · swapCrop  — a new photo in the window: the window of it that keeps every
//     face whole (the worker's expand.faceSafeCrop, the same maths — the
//     worker's themes test proves it), else the centred cover window and a
//     warning: the client decides;
//   · panCrop / zoomCrop — she moves and zooms the photo INSIDE the window:
//     the crop slides / shrinks with the window's own shape, never past the
//     photo's edge, never smaller than ZOOM_MAX × in;
//   · headsCut — the warning: a head the window cuts (never a refusal).
//
// A crop is the window into the FULL photo, 0..1 (PageElement.crop). Its
// shape in pixels is always the box's — so the editor and the preview
// (cropCss) and the print (cropSource) show exactly it: cropMath.ts.
//
// Pure. No DOM.

import type { Crop } from './cropMath';

export interface WindowPhoto {
  width: number;
  height: number;
  /** Detector face boxes, px of width × height (photos.face_data). */
  faces?: ReadonlyArray<{ x: number; y: number; width: number; height: number }> | null;
}

/** The page the elements' percentages are of (A4 trim, mm). */
export const PAGE_W_MM = 200;
export const PAGE_H_MM = 297;
/** A photo element's real aspect (w / h) from its box in % of the page. */
export function boxAspect(el: { width: number; height: number }): number {
  return (el.width * PAGE_W_MM) / Math.max(1e-9, el.height * PAGE_H_MM);
}

// The worker's numbers (worker/src/lib/themes/expand.ts) — kept in step by its themes test.
const MIN_FACE_FRAC = 0.04;
const FACE_PAD = 0.18;
export const HEAD_MARGIN = 0.25;
/** How far in the client may zoom: the window at its smallest is 1 / ZOOM_MAX of the cover window. */
export const ZOOM_MAX = 4;

const r5 = (v: number) => Math.round(v * 1e5) / 1e5;
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

function realFaces(p: WindowPhoto) {
  return (p.faces ?? []).filter((f) => f && f.width > 0 && f.height > 0 && f.height >= p.height * MIN_FACE_FRAC
    && f.x > p.width * 0.01 && f.y > p.height * 0.01 && f.x + f.width < p.width * 0.99 && f.y + f.height < p.height * 0.99);
}
function headFaces(p: WindowPhoto) {
  return (p.faces ?? []).filter((f) => f && f.width > 0 && f.height > 0 && f.height >= p.height * MIN_FACE_FRAC);
}

/** The largest window of the box's shape the photo holds, centred (what a renderer shows with no crop). */
export function coverWindow(p: Pick<WindowPhoto, 'width' | 'height'>, boxAR: number): Crop {
  const ar = p.width / p.height;
  return ar > boxAR ? { x: r5((1 - boxAR / ar) / 2), y: 0, w: r5(boxAR / ar), h: 1 } : { x: 0, y: r5((1 - ar / boxAR) / 2), w: 1, h: r5(ar / boxAR) };
}

/**
 * The cover window that keeps every real face inside (padded, tight if the pad
 * does not fit) and no head cut — null when none does. The worker's
 * expand.faceSafeCrop, line for line.
 */
export function faceSafeWindow(p: WindowPhoto, boxAR: number): Crop | null {
  const ar = p.width / p.height;
  const faces = realFaces(p);
  const horizontal = ar > boxAR;
  const size = horizontal ? boxAR / ar : ar / boxAR;
  const span = (pad: number): [number, number] => {
    let a = 1, b = 0;
    for (const f of faces) {
      const lo = horizontal ? (f.x - f.width * pad) / p.width : (f.y - f.height * pad) / p.height;
      const hi = horizontal ? (f.x + f.width * (1 + pad)) / p.width : (f.y + f.height * (1 + pad)) / p.height;
      a = Math.min(a, lo); b = Math.max(b, hi);
    }
    return [Math.max(0, a), Math.min(1, b)];
  };
  let start = (1 - size) / 2;
  if (faces.length) {
    let [a, b] = span(FACE_PAD);
    if (b - a > size) [a, b] = span(0);
    if (b - a > size + 1e-9) return null;
    start = Math.min(Math.max((a + b) / 2 - size / 2, Math.max(0, b - size)), Math.min(a, 1 - size));
    start = Math.min(Math.max(start, 0), 1 - size);
  }
  const win = horizontal ? { x: start, y: 0, w: size, h: 1 } : { x: 0, y: start, w: 1, h: size };
  // the worker's head test, exactly: sides and top
  for (const f of headFaces(p)) {
    const hx0 = (f.x - f.width * HEAD_MARGIN) / p.width, hx1 = (f.x + f.width * (1 + HEAD_MARGIN)) / p.width;
    const hy0 = (f.y - f.height * HEAD_MARGIN) / p.height;
    if (hx0 < win.x - 1e-6 || hx1 > win.x + win.w + 1e-6 || hy0 < win.y - 1e-6) return null;
  }
  return { x: r5(win.x), y: r5(win.y), w: r5(win.w), h: r5(win.h) };
}

/**
 * Does `crop` cut a head — the face box widened by HEAD_MARGIN on the sides and
 * above, the worker's rule (expand.faceSafeCrop, rules G: a chin below the
 * window's bottom edge is the window's business)? A head the window leaves out
 * entirely counts too: that person is no longer on the page.
 */
export function headsCut(p: WindowPhoto, crop: Crop): boolean {
  if (!(p.width > 0 && p.height > 0)) return false;
  return headFaces(p).some((f) => {
    const hx0 = (f.x - f.width * HEAD_MARGIN) / p.width, hx1 = (f.x + f.width * (1 + HEAD_MARGIN)) / p.width;
    const hy0 = (f.y - f.height * HEAD_MARGIN) / p.height, fy0 = f.y / p.height;
    return hx0 < crop.x - 1e-6 || hx1 > crop.x + crop.w + 1e-6 || hy0 < crop.y - 1e-6 || fy0 > crop.y + crop.h - 1e-6;
  });
}

/**
 * headsCut for ONE HALF of a panorama (S5 — the photo runs on across the spine
 * onto the facing page): a head wholly on the other half is not on this page,
 * and the spine edge is not a cut (the photo goes on there). Only the outer
 * edge, the top and the bottom can cut a head.
 */
export function headsCutOnHalf(p: WindowPhoto, crop: Crop, half: 'left' | 'right'): boolean {
  if (!(p.width > 0 && p.height > 0)) return false;
  return headFaces(p).some((f) => {
    const hx0 = (f.x - f.width * HEAD_MARGIN) / p.width, hx1 = (f.x + f.width * (1 + HEAD_MARGIN)) / p.width;
    const hy0 = (f.y - f.height * HEAD_MARGIN) / p.height, fy0 = f.y / p.height;
    if (half === 'left' ? hx0 >= crop.x + crop.w - 1e-6 : hx1 <= crop.x + 1e-6) return false;   // on the other page
    const outer = half === 'left' ? hx0 < crop.x - 1e-6 : hx1 > crop.x + crop.w + 1e-6;
    return outer || hy0 < crop.y - 1e-6 || fy0 > crop.y + crop.h - 1e-6;
  });
}

/** A new photo in the window: its face-safe window, else the centred one with `faceCut` (a warning). */
export function swapCrop(p: WindowPhoto, boxAR: number): { crop: Crop; faceCut: boolean } {
  const safe = faceSafeWindow(p, boxAR);
  return safe ? { crop: safe, faceCut: false } : { crop: coverWindow(p, boxAR), faceCut: true };
}

/** The crop a photo element shows: its saved one, else the centred cover window (a renderer's default). */
export function currentCrop(crop: Crop | null | undefined, p: Pick<WindowPhoto, 'width' | 'height'>, boxAR: number): Crop {
  return crop && crop.w > 0 && crop.h > 0 ? crop : coverWindow(p, boxAR);
}

/** Keep a window inside the photo. */
function inside(c: Crop): Crop {
  const w = clamp(c.w, 1e-4, 1), h = clamp(c.h, 1e-4, 1);
  return { x: r5(clamp(c.x, 0, 1 - w)), y: r5(clamp(c.y, 0, 1 - h)), w: r5(w), h: r5(h) };
}

/**
 * Move the photo inside its window by (dx, dy) box widths / heights — the photo
 * follows the finger: dragging right shows more of its left side. Stops at the
 * photo's edges.
 */
export function panCrop(crop: Crop, dx: number, dy: number): Crop {
  return inside({ x: crop.x - dx * crop.w, y: crop.y - dy * crop.h, w: crop.w, h: crop.h });
}

/**
 * Zoom by `factor` (> 1 = closer) about the point (ax, ay) of the window (0..1,
 * default its centre), keeping the window's shape. Never wider than the cover
 * window (the photo always fills its window), never closer than ZOOM_MAX ×.
 */
export function zoomCrop(crop: Crop, p: Pick<WindowPhoto, 'width' | 'height'>, boxAR: number, factor: number, ax = 0.5, ay = 0.5): Crop {
  const cover = coverWindow(p, boxAR);
  const scale = clamp(crop.w / Math.max(1e-9, factor), cover.w / ZOOM_MAX, cover.w) / crop.w;
  const w = crop.w * scale, h = crop.h * scale;
  const px = crop.x + ax * crop.w, py = crop.y + ay * crop.h;     // the point under the fingers stays put
  return inside({ x: px - ax * w, y: py - ay * h, w, h });
}

/** How far in the photo is zoomed (1 = the cover window). */
export function zoomOf(crop: Crop, p: Pick<WindowPhoto, 'width' | 'height'>, boxAR: number): number {
  return coverWindow(p, boxAR).w / Math.max(1e-9, crop.w);
}

/** photos.face_data → the boxes (anything malformed dropped). */
export function facesFrom(raw: unknown): WindowPhoto['faces'] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((f) => f && typeof f === 'object').map((f) => {
    const o = f as Record<string, unknown>;
    return { x: Number(o.x), y: Number(o.y), width: Number(o.width), height: Number(o.height) };
  }).filter((f) => [f.x, f.y, f.width, f.height].every(Number.isFinite) && f.width > 0 && f.height > 0);
}
